import { useEffect, useState } from "react";

/**
 * Track whether the tab is visible.
 *
 * Polling a hidden tab burns a laptop's battery and asks the console questions
 * nobody is reading the answers to, so every live query multiplies its
 * interval by this hook.
 *
 * Returns:
 *     True while the document is visible.
 */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(
    () =>
      typeof document === "undefined" || document.visibilityState !== "hidden",
  );

  useEffect(() => {
    const onChange = (): void => {
      setVisible(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return visible;
}

/**
 * Turn a poll period into a react-query `refetchInterval`.
 *
 * Args:
 *     milliseconds: Desired period.
 *     visible: Whether the tab is visible.
 *
 * Returns:
 *     The period, or `false` to stop polling while the tab is hidden.
 */
export function pollInterval(
  milliseconds: number,
  visible: boolean,
): number | false {
  return visible ? milliseconds : false;
}
